import React from 'react';
import { Helmet } from 'react-helmet-async';

const someDefinedVariable = "Refund Policy | DocuSafe | Hassle-Free Subscription Refunds";

const RefundPolicy = () => {
    return (
        <>
            <Helmet>
                <title>{someDefinedVariable || "Default Title"}</title>
                <meta name='description' content='Read the refund policy for DocuSafe your trusted e-signature and document security platform. Learn about refund eligibility, the request process, and our support details. Stay informed about secure digital documentation and easy subscription management.' />
                <meta name='keywords' content='Refund policy, DocuSafe refund, subscription refund, e-signature refund, digital security refund, cancel subscription, online document security, hassle-free refunds, digital signature service refund, high volume low competition, trending legal policies, easy refund process, low competition keywords' />
            </Helmet>
            <section className="policy-container">
                <div className="container">
                    <div className="policy-section">
                        <h2 className="policy-title">Refund Policy</h2>
                        <div className="policy-intro">
                            <p className="policy-text">
                                <strong>DocuSafe.ai</strong>, including its e-signature platform, SignEase.ai, operates on a subscription-based model. This Refund Policy outlines the circumstances under which refunds may be issued and the process for requesting them.
                            </p>
                            <p className="policy-text">
                                For any refund-related inquiries, contact us at{' '}
                                <a href="mailto:support@DocuSafe.ai" className="policy-link">
                                    support@docusafe.ai
                                </a>
                            </p>
                            <p className="policy-text">
                                <strong>Redfox Ventures Pte. Ltd., 200 Jalan Sultan, #11-01, Textile Centre, Singapore 199018.</strong>
                            </p>
                        </div>
                    </div>

                    <div className="policy-section">
                        <h3 className="policy-subtitle">1. General Policy</h3>
                        <div className="policy-card">
                            <h4 className="policy-card-title">1.1 Subscription Model</h4>
                            <p className="policy-text">
                                DocuSafe.ai services are billed on a prepaid basis, either monthly or annually, depending on the chosen subscription plan.
                            </p>
                        </div>

                        <div className="policy-card">
                            <h4 className="policy-card-title">1.2 Refund Eligibility</h4>
                            <p className="policy-text">Refunds are only issued under the following circumstances:</p>
                            <ul className="policy-list">
                                <li className="policy-text">If the Customer cancels their subscription due to material changes in the Terms and Conditions or Privacy Policy within 30 days of the change notification.</li>
                                <li className="policy-text">If technical issues caused by DocuSafe.ai prevent the use of the platform, and these issues remain unresolved despite reasonable efforts by our support team.</li>
                            </ul>
                        </div>

                        <div className="policy-card">
                            <h4 className="policy-card-title">1.3 No Refund for Unused Time</h4>
                            <p className="policy-text">
                                DocuSafe.ai does not offer refunds for unused portions of a subscription if the account is canceled voluntarily before the end of the billing period.
                            </p>
                        </div>
                    </div>

                    <div className="policy-section">
                        <h3 className="policy-subtitle">2. Refund Request Process</h3>
                        <div className="policy-card">
                            <h4 className="policy-card-title">2.1 How to Request a Refund</h4>
                            <p className="policy-text">Submit a refund request by emailing{' '}
                                <a href="mailto:support@DocuSafe.ai" className="policy-link">
                                    support@docusafe.ai
                                </a>
                            </p>
                            <p className="policy-text">Include the following details:</p>
                            <ul className="policy-list">
                                <li className="policy-text">Account details (name and email address)</li>
                                <li className="policy-text">Reason for the refund request</li>
                                <li className="policy-text">Supporting documentation, if applicable</li>
                            </ul>
                        </div>

                        <div className="policy-card">
                            <h4 className="policy-card-title">2.2 Review Timeline</h4>
                            <p className="policy-text">
                                Refund requests will be reviewed within <strong>7 business days</strong> of receipt.
                            </p>
                        </div>

                        <div className="policy-card">
                            <h4 className="policy-card-title">2.3 Approval and Issuance</h4>
                            <p className="policy-text">
                                If approved, refunds will be processed within <strong>10 business days</strong> using the original payment method.
                            </p>
                        </div>
                    </div>

                    <div className="policy-section">
                        <h3 className="policy-subtitle">3. Exceptions</h3>
                        <div className="policy-card">
                            <h4 className="policy-card-title">3.1 Ineligible Refunds</h4>
                            <p className="policy-text">The following are not eligible for refunds:</p>
                            <ul className="policy-list">
                                <li className="policy-text">Payments for services already rendered or completed (e.g., one-time document processing fees)</li>
                                <li className="policy-text">Subscriptions canceled after the renewal date without prior notice</li>
                            </ul>
                        </div>

                        <div className="policy-card">
                            <h4 className="policy-card-title">3.2 Trial Periods</h4>
                            <p className="policy-text">
                                If you subscribe to a paid plan after using a free trial, no refund will be issued unless technical issues (as outlined in 1.2) occur.
                            </p>
                        </div>
                    </div>

                    <div className="policy-section">
                        <h3 className="policy-subtitle">4. Disputes and Chargebacks</h3>
                        <div className="policy-card">
                            <h4 className="policy-card-title">4.1 Resolution First</h4>
                            <p className="policy-text">
                                Customers are encouraged to contact{' '}
                                <a href="mailto:support@DocuSafe.ai" className="policy-link">
                                    support@docusafe.ai
                                </a>{' '}
                                to resolve any issues before initiating a chargeback or dispute through their payment provider.
                            </p>
                        </div>

                        <div className="policy-card">
                            <h4 className="policy-card-title">4.2 Fraudulent Chargebacks</h4>
                            <p className="policy-text">
                                DocuSafe reserves the right to suspend or terminate accounts associated with fraudulent chargebacks.
                            </p>
                        </div>
                    </div>

                    <div className="policy-section">
                        <h3 className="policy-subtitle">5. Changes to the Refund Policy</h3>
                        <p className="policy-text">
                            DocuSafe.ai may update this Refund Policy as needed. Customers will be notified of any material changes, and continued use of the platform constitutes acceptance of the updated policy.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
};

export default RefundPolicy;